import { BufferstockService, StockMovementService, StockObjectService } from 'src/packages/warehouse';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { UserHelper } from '../../app/providers/helpers/user-helper.service';
import { ModelAdapters } from './interface/model-adapters';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MachineService } from '../../app/providers/model-services/machines/machine.service';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { ArticleService } from '../../app/providers/model-services/article.service';
import type { BookingClientIds } from './booking-client-ids';
import type { StockMovementModel } from 'src/packages/warehouse';

/**
 * Pro Buchung übergebener Kontext: Quelle, Ziel und optionale Flags. Eine
 * Buchung verarbeitet genau eine Position.
 *
 * `targetId` ist optional, weil reine Disposal-Targets (TRASH_BIN,
 * CONSUMPTION_BIN) keine ID tragen.
 */
export interface BookingContext {
  sourceType: StockSourceType;
  targetType: StockTargetType;
  sourceId: number;
  targetId?: number;
  intent: BookingIntent;
  transactionId?: BookingTransactionId;
  /**
   * Aktivität, in der sich der User gerade befindet (Service- oder Befüll-Aktion).
   */
  actionContext?: ActionContextType;
  /** Anmerkung des Users (z.B. Begründung einer Korrektur-Buchung). */
  description?: string;
  /**
   * Beleg-/Gruppierungs-Referenz, über die die Büroware zusammengehörige
   * Movements erkennt (`Order <Nr>` bzw. `Transfer <uuid>`, siehe
   * `BookingReference`). Wird für jedes erzeugte Movement gesetzt.
   */
  bookingReference?: string;
}

/**
 * System-weite Konfiguration, die zur Laufzeit von den Funktionsschaltern
 * abgeleitet wird. Pro `BookingOrchestrator`-Instanz fix.
 */
export interface SystemContext {
  /** Funktionsschalter `CAR_MANAGEMENT`. */
  carActive: boolean;
  /** Funktionsschalter `SO_USE_BUFFERSTOCKS`. */
  bufferstockActive: boolean;
  /** Funktionsschalter `CM_ALLOW_CAR_RELOCATION`. */
  carRelocationActive: boolean;
  /** Funktionsschalter `NEGATIVE_POSITIONS` */
  allowNegativePositionAmounts: boolean;
}

/**
 * Von den Adaptern benötigten App-Services.
 */
export interface BookingServices {
  articleService: ArticleService;
  stockObjectService: StockObjectService;
  stockMovementService: StockMovementService;
  bufferstockService: BufferstockService;
  machineService: MachineService;
  userHelper: UserHelper;
}

/**
 * Gesamter Kontext einer Buchung: der pro Buchung übergebene `BookingContext`
 * plus der zur Laufzeit abgeleitete `SystemContext`.
 */
export interface Context {
  booking: BookingContext;
  system: SystemContext;
}

/**
 * Minimaler struktureller Vertrag einer buchbaren Position: jedes Modell mit
 * `article_id` und `amount` ist via struktureller Typisierung zuweisbar, ohne
 * Kopplung an die Page-Interfaces.
 */
export interface BookingPosition {
  article_id: number;
  amount: number;
  /** Optional - nur für Validierungs-/Fehlermeldungen. */
  article_name?: string;
  /** Optional - nur für Validierungs-/Fehlermeldungen. */
  article_number?: string;
  /** Optional - nur Korrelation in Validation-Summaries. */
  id?: number;
}

/**
 * Eingabeparameter einer Buchung: Kontext, Adapter und stabile client_ids.
 */
export interface BookingParams {
  context: Context;
  adapters: ModelAdapters;
  /**
   * Stabile `client_id` pro Position dieser Buchung. Lebt auf dem
   * `BookingRequest` (bzw. beim Rollback auf dem Queue-Eintrag), damit
   * Wiederholungen desselben Requests dieselben IDs senden.
   */
  clientIds: BookingClientIds;
}

/**
 * Schweregrad eines Constraints: blockierend (Buchung unterbunden) oder Warnung.
 */
export enum ConstraintLevel {
  BLOCKING = 'blocking',
  WARNING = 'warning',
}

/**
 * Ergebnis eines geprüften Constraints mit Schweregrad, Erfüllt-Status und
 * lokalisierter Meldung.
 */
export interface ConstraintSummary {
  level: ConstraintLevel;
  satisfied: boolean;
  message: string;
}

/**
 * Auswertung einer Position im Kontext: blockierende und warnende Constraints.
 */
export interface PositionContextSummary {
  position: BookingPosition;
  context: Context;
  blocking: ConstraintSummary[];
  warnings: ConstraintSummary[];
}

/**
 * Ergebnis von `BookingOrchestrator.book()` (und der einzelnen Actions).
 * Bei `success: false` enthält `message` den Grund.
 */
export interface BookingResult {
  success: boolean;
  message: string;
  /**
   * Nur bei `success: false` relevant: true bei transienter Ursache
   * (Netz/Timeout/Lock); derselbe Request kann unverändert wiederholt werden
   * (stabile client_ids). false/undefined = permanent (z.B. Validierung).
   */
  retryable?: boolean;
}

/**
 * Flaches Ergebnis eines Remote-Aufrufs der Adapter. Keine Discriminated
 * Union: der App-Build läuft ohne strictNullChecks, dort greift Narrowing nicht.
 */
export interface RemoteResult<T> {
  /** Server-Antwort; null bei Fehlschlag. */
  value: T | null;
  /** Nur bei Fehlschlag relevant: transienter Fehler, Wiederholung sinnvoll. */
  retryable: boolean;
}

/**
 * Ergebnis eines Phase-2-Commits (`bookRemote`). `alreadyBooked` (HTTP 409) =
 * Ziel-Zustand existiert bereits: für Recovery/Retry ein Erfolg (Marker
 * entfernen), für eine User-Annahme ein permanenter Konflikt.
 */
export interface BookRemoteResult {
  booked: boolean;
  alreadyBooked: boolean;
  /** Nur wenn beides false: transienter Fehler, Wiederholung sinnvoll. */
  retryable: boolean;
}

/**
 * Aktivität, in der sich der User gerade befindet.
 */
export enum ActionContextType {
  SERVICE = 'service',
  FILLING = 'filling',
}

/**
 * Mögliche Quell-Lagertypen einer Buchung.
 */
export enum StockSourceType {
  CAR = 'car',
  BUFFERSTOCK = 'bufferstock',
  MACHINE = 'machine',
}

/**
 * Mögliche Ziel-Lagertypen einer Buchung, inkl. reiner Disposal-Ziele
 * (Müll/Verbrauch), die keine ID tragen.
 */
// Cleaner wäre, hier StockSourceType zu extenden, in TS aber leider nicht möglich
export enum StockTargetType {
  CAR = 'car',
  BUFFERSTOCK = 'bufferstock',
  MACHINE = 'machine',
  TRASH_BIN = 'trash_bin',
  CONSUMPTION_BIN = 'consumption_bin',
  BUFFERSTOCK_DISPOSAL = 'bufferstock_disposal',
}

/**
 * Fachliche Absicht einer Buchung, steuert Ziel- und Movement-Wahl.
 */
export enum BookingIntent {
  MOVE = 'move',
  ORDER = 'order',
  TRASH = 'trash',
  CONSUMPTION = 'consumption',
  BUFFERSTOCK_DISPOSAL = 'bufferstock_disposal',
}

/**
 * Bezeichner einer Buchungs-Transaktion, unter dem mehrere Buchungen
 * zusammengefasst und ggf. gemeinsam zurückgerollt werden.
 */
export type BookingTransactionId = number;

declare const incomingTransferBrand: unique symbol;

/**
 * Eingehendes, noch unbestätigtes Movement für die Empfangsbestätigung.
 *
 * Branded Type: nicht hand-konstruierbar, nur die `findIncoming*Transfers` des
 * `MovementAdapter` erzeugen sie. So bestätigen die `acceptIncoming*Transfer`
 * compile-time-gesichert ausschließlich server-gelieferte Movements.
 */
export type IncomingTransfer = StockMovementModel & {
  readonly [incomingTransferBrand]: true;
};

/**
 * Persistierter Marker für ein zweiphasiges Movement, dessen Phase 2
 * (`bookRemote`) noch aussteht. Wird nach erfolgreicher Phase 1 geschrieben
 * und nach erfolgreicher Phase 2 entfernt; überlebt App-Neustarts.
 * `id` ist die Remote-ID des Phase-1-Movements.
 */
export interface PendingPhase2Commit {
  id: number;
  /** `client_id` des Movements - nur für Diagnose/Korrelation. */
  clientId?: string;
}

/**
 * In einer Transaktion eingereihte Einzelbuchung: persistierte `entryId`,
 * die Position und ihr Buchungskontext.
 */
export interface TransactionQueuedBooking {
  entryId: string;
  position: BookingPosition;
  booking: BookingContext;
}